import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { updateFields, updateFormProperties } from 'apps/auth/actions/formAction';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelContent, PanelFrame, PanelRightBlock } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getRecipientTypeParam, transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { SENDING_METHOD_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    PAYMENT_COUNTRY,
    PAYMENT_CURRENCY,
    PAYMENT_FLOW,
    PAYMENT_METHOD_AMPLITUDE,
    PAYMENT_METHOD_CATEGORY,
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
    PAYOUT_METHOD,
    PAYOUT_METHOD_CATEGORY,
    RECIPIENT_TYPE,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { PAYMENT_BONUS, PAYMENT_GLOBAL_ACCOUNT } from 'apps/auth/constants/Constants';
import { PAYMENT_CATEGORY, SHOW_CARD_ERROR_POPUP } from 'apps/auth/constants/ExtraNames';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_SEND_CHANGE_PAYMENT_METHOD } from 'apps/auth/constants/UrlConstants';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { isCardExpired, toLowerCase } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import DeleteCardPopup from '../popups/DeleteCardPopup';

const EnabledPaymentMethod = ({ isShowBadgeBanner = true, badge = '', actionBanner = '', isIncomplete = false, ...props }) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const { deliveryMethod, toCountry, fromCountry, toCurrency, fromCurrency, payoutCategory, paymentCategory, showCardErrorPopUp, binInfo } = useExtra();
    const { continueTransferFlow, changePaymentOption, dcSourceExpired, gotIt } = usePageMessages();
    const amplitudeSendingMethod = transactionMethods[props.method];
    const amplitudeDeliveryMethod = transactionMethods[deliveryMethod];
    const isExpired = isCardExpired(props.card);

    useEffect(() => {
        if (showCardErrorPopUp && (binInfo?.warningAllowed || binInfo?.warningRestricted)) {
            const text = binInfo?.error;
            props.popupRef.current?.open(
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={text} />} popupRef={props.popupRef}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={changePaymentOption}
                        onClick={() => {
                            props.popupRef.current?.close();
                        }}
                    />
                    {binInfo?.warningAllowed && (
                        <PopupButton
                            iconName="check"
                            text={gotIt}
                            onClick={() => {
                                const url = `${AUTH_SEND_CHANGE_PAYMENT_METHOD}${toLowerCase(props.toCountryCode)}/${toLowerCase(props.toCurrencyCode)}/${deliveryMethod}?type=${props.recipientType}&isIncomplete=${isIncomplete}&warningAllowed=true`;
                                props.updateFormProperties(SEND_FORM_NAME, url, false).then(() => {
                                    props.submitRef.current.click();
                                    props.popupRef.current?.close();
                                });
                            }}
                        />
                    )}
                </SimplePopup>,
            );
            props.updateExtraByName(SHOW_CARD_ERROR_POPUP, false);
        }
    }, [showCardErrorPopUp]);

    const getTitle = (name, description) => {
        if (description) {
            return (
                <>
                    <Text>{name}</Text>
                    <br />
                    <Text size="secondary" color="secondary">
                        {description}
                    </Text>
                </>
            );
        } else {
            return <Text color={isExpired ? 'lightGray' : null}>{name}</Text>;
        }
    };

    const onPanelClick = (e) => {
        if (isExpired) {
            props.popupRef.current?.open(<DeleteCardPopup selectedCard={props.card} fromDcAccount={true} popupRef={props.popupRef} />);
        } else {
            onClickHandler(e);
        }
    };

    const getName = (method = '', paymentMethod = '', paymentName = '') => {
        if (method === PAYMENT_BONUS || method === PAYMENT_GLOBAL_ACCOUNT) {
            return `${getLocalizedRawAmount(lang, country, paymentMethod?.balance)} ${paymentMethod?.currencyIso}`;
        }
        return paymentName;
    };

    const clickHandler = (e) => {
        if (props.clickHandler) {
            props.clickHandler(e, props.method, props.code, props.category);
        }
        if (paymentCategory !== props.paymentMethodCategory) {
            props.updateExtraByName(PAYMENT_CATEGORY, props.paymentMethodCategory);
        }
        amplitude.getInstance().logEvent(SENDING_METHOD_SELECTED, {
            [PAYOUT_COUNTRY]: toCountry?.nameEn,
            [PAYMENT_COUNTRY]: fromCountry?.nameEn,
            [PAYOUT_METHOD]: amplitudeDeliveryMethod,
            [PAYMENT_METHOD_AMPLITUDE]: amplitudeSendingMethod,
            [PAYOUT_METHOD_CATEGORY]: payoutCategory,
            [PAYMENT_METHOD_CATEGORY]: props.paymentMethodCategory,
            [PAYMENT_FLOW]: props.getPaymentFlow?.(),
            [RECIPIENT_TYPE]: getRecipientTypeParam(props.recipientType),
            [PAYOUT_CURRENCY]: toCurrency?.code,
            [PAYMENT_CURRENCY]: fromCurrency?.code,
        });
    };

    const onClickHandler = (e) => {
        e.preventDefault();
        if (isShowBadgeBanner && actionBanner) {
            props.popupRef.current?.open(
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={actionBanner} />} popupRef={props.popupRef}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={continueTransferFlow}
                        onClick={() => {
                            clickHandler(e);
                            props.popupRef.current?.close();
                        }}
                    />
                    <PopupButton
                        iconName="retry"
                        text={changePaymentOption}
                        onClick={() => {
                            props.popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        } else {
            clickHandler(e);
        }
    };

    return (
        <Panel onClick={onPanelClick} negativeMargin isHoverable>
            <PanelFrame>
                <DiskBadgeWrapper>
                    <DiskBadge
                        iconName={props.icon ? props.icon : null}
                        src={props.src ? props.src : null}
                        variant={props.diskVariant ? props.diskVariant : 'secondary'}
                        withoutRadius={props.withoutRadius}
                        isDisabled={isExpired}
                    />
                </DiskBadgeWrapper>
                <PanelContent>{getTitle(getName(props.method, props.paymentMethod, props.name), props.description)}</PanelContent>
                <PanelRightBlock>
                    {isExpired || (isShowBadgeBanner && badge) ? (
                        <Badge variant={isExpired ? 'tertiary' : 'primary'} caption={isExpired ? dcSourceExpired : badge} />
                    ) : null}
                </PanelRightBlock>
            </PanelFrame>
        </Panel>
    );
};
export default connect(null, { updateExtraByName, updateFields, updateFormProperties })(EnabledPaymentMethod);
